import { useMutation } from 'react-query';
import { assetsClipPublishToLibraryCreate } from '@goldcast/api/content';
import { useMemo, useSyncExternalStore } from 'react';
import { getIsDuplicateRecording } from '@goldcast/player-config/src/utils/clipUtils';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { useAppContext } from '@/context/AppContext/AppContext';
import useAnalytics from '@/hooks/useAnalytics';
import { showErrorToast, showSuccessToast } from '@/libs/toast/toast';
import { updateClipMetadataWithId } from '@/stores/clip';
import featureFlagStore from '@/stores/featureFlagStore';

export default function usePublishAsset() {
  const { clipId, clipData } = useClipsContext();
  const { logger } = useAppContext();
  const { trackVideoPublished } = useAnalytics();
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);

  const shouldEnableLayers = useMemo(() => {
    const clipLayout = clipData?.asset_metadata?.layout;
    const isAudiogram = clipLayout === 'AUDIOGRAM';

    if (isAudiogram) {
      return false;
    }

    const isSpeakerLayout = clipLayout === 'SPEAKER' || clipLayout === 'GRID';
    const showSpeakerLabels = !!clipData?.asset_metadata?.magicLayout?.showSpeakerLabels;

    return (isSpeakerLayout && showSpeakerLabels) || !!clipData?.asset_metadata?.captions?.length;
  }, [
    clipData?.asset_metadata?.captions,
    clipData?.asset_metadata?.layout,
    clipData?.asset_metadata?.magicLayout?.showSpeakerLabels,
    clipData?.content?.media_source_type,
    featureFlags
  ]);

  const { mutateAsync: publishAsset, isLoading: isPublishing } = useMutation(
    async () => {
      const res = await assetsClipPublishToLibraryCreate({
        id: clipId,
        body: {
          enable_layers: shouldEnableLayers
        }
      });
      return res;
    },
    {
      onSuccess: res => {
        updateClipMetadataWithId(clipId, res.asset_metadata);
        showSuccessToast('Publishing started. This might take a few minutes.');
        trackVideoPublished({ id: clipId, isSuccess: true, isDuplicate: getIsDuplicateRecording(clipData) });
      },
      onError: err => {
        logger.error('Failed to publish clip to library:', err);
        showErrorToast('Something went wrong while publishing recording. Please try again later.');
        trackVideoPublished({ id: clipId, isSuccess: false, isDuplicate: getIsDuplicateRecording(clipData) });
      }
    }
  );

  return { publishAsset, isPublishing };
}
